/**
 * Centralized logging for Canasta frontend.
 *
 * Log Levels: OFF (0), NORMAL (1), VERBOSE (2)
 *
 * Configuration:
 *   - Stored in localStorage for persistence
 *   - Logger.setLevel(LogLevel.VERBOSE) - Set global level
 *   - Logger.setModuleLevel(LogCategory.WEBRTC, LogLevel.VERBOSE) - Set per-module level
 *
 * Usage:
 *   Logger.info(LogCategory.GAME, 'Player joined', playerName);
 *   Logger.debug(LogCategory.SOCKET, 'Message received', data);
 *   Logger.warn(LogCategory.WEBRTC, 'Connection unstable');
 *   Logger.error(LogCategory.AUTH, 'Login failed', error);
 *
 * Backend Logging:
 *   - All logs are automatically sent to backend for unified log files
 *   - Logs are buffered and sent in batches every 5 seconds (or 50 logs)
 *   - Logs are flushed on page unload/visibility change
 *   - View unified logs in: backend/logs/canasta.log
 *   - Logger.setBackendLogging(false) - Disable backend logging
 *   - Logger.flush() - Force immediate log flush
 */

/**
 * Log levels as const object (erasableSyntaxOnly compatible)
 */
export const LogLevel = {
  OFF: 0,
  NORMAL: 1,
  VERBOSE: 2
} as const
export type LogLevel = typeof LogLevel[keyof typeof LogLevel]

/**
 * Log categories as const object (erasableSyntaxOnly compatible)
 */
export const LogCategory = {
  GAME: 'GAME',
  SOCKET: 'SOCKET',
  WEBRTC: 'WEBRTC',
  VIDEO: 'VIDEO',
  CARDS: 'CARDS',
  AUDIO: 'AUDIO',
  AUTH: 'AUTH',
  UI: 'UI',
  DEFAULT: 'DEFAULT'
} as const
export type LogCategory = typeof LogCategory[keyof typeof LogCategory]

/**
 * Internal log level names for backend communication
 */
type LogLevelName = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

/**
 * Log entry structure for backend
 */
interface LogEntry {
  level: LogLevelName;
  category: string;
  message: string;
  timestamp: string;
}

/**
 * Logger configuration object
 */
interface LoggerConfig {
  globalLevel: string;
  moduleOverrides: Record<string, string>;
}

/**
 * Scoped logger interface
 */
interface ScopedLogger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

/**
 * Centralized logging class with backend synchronization
 */
class LoggerClass {
  // Configuration constants
  private readonly BACKEND_LOG_ENDPOINT = '/api/debug/frontend-logs';
  private readonly LOG_BUFFER_SIZE = 50;  // Flush when buffer reaches this size
  private readonly LOG_FLUSH_INTERVAL = 5000;  // Flush every 5 seconds

  // State
  private globalLevel: LogLevel;
  private moduleOverrides: Record<string, LogLevel> = {};
  private logBuffer: LogEntry[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private backendLoggingEnabled = true;

  // Category colors for visual distinction in console
  private readonly COLORS: Record<string, string> = {
    [LogCategory.GAME]: '#4CAF50',    // Green
    [LogCategory.SOCKET]: '#2196F3',  // Blue
    [LogCategory.WEBRTC]: '#9C27B0',  // Purple
    [LogCategory.VIDEO]: '#FF9800',   // Orange
    [LogCategory.CARDS]: '#E91E63',   // Pink
    [LogCategory.AUDIO]: '#00BCD4',   // Cyan
    [LogCategory.AUTH]: '#795548',    // Brown
    [LogCategory.UI]: '#3F51B5',      // Indigo
    [LogCategory.DEFAULT]: '#607D8B'  // Blue-grey
  };

  // Log level to minimum required logger level mapping
  private readonly LOG_LEVEL_VALUES: Record<LogLevelName, LogLevel> = {
    'DEBUG': LogLevel.VERBOSE,  // DEBUG requires VERBOSE (2)
    'INFO': LogLevel.NORMAL,    // INFO requires NORMAL (1)
    'WARN': LogLevel.NORMAL,    // WARN requires NORMAL (1)
    'ERROR': LogLevel.NORMAL    // ERROR requires NORMAL (1)
  };

  constructor() {
    // Load configuration from localStorage or use defaults
    const storedLevel = this.getStorageItem('LOG_LEVEL');
    this.globalLevel = storedLevel ? this.parseLogLevel(storedLevel) : LogLevel.NORMAL;

    try {
      const storedModules = this.getStorageItem('LOG_MODULES');
      const parsed = storedModules ? JSON.parse(storedModules) : {};
      this.moduleOverrides = parsed;
    } catch (e) {
      this.moduleOverrides = {};
    }

    // Setup flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flush());
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flush();
        }
      });
    }
  }

  // =========================================
  // Private Helper Methods
  // =========================================

  /**
   * Get item from localStorage (allows mocking in tests)
   */
  private getStorageItem(key: string): string | null {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  }

  /**
   * Set item in localStorage (allows mocking in tests)
   */
  private setStorageItem(key: string, value: string): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key, value);
  }

  /**
   * Parse log level from string
   */
  private parseLogLevel(level: string): LogLevel {
    const upperLevel = level.toUpperCase();
    if (upperLevel === 'OFF') return LogLevel.OFF;
    if (upperLevel === 'NORMAL') return LogLevel.NORMAL;
    if (upperLevel === 'VERBOSE') return LogLevel.VERBOSE;
    return LogLevel.NORMAL;
  }

  /**
   * Convert log level to string
   */
  private levelToString(level: LogLevel): string {
    switch (level) {
      case LogLevel.OFF: return 'OFF';
      case LogLevel.NORMAL: return 'NORMAL';
      case LogLevel.VERBOSE: return 'VERBOSE';
      default: return 'NORMAL';
    }
  }

  /**
   * Get effective log level for a category
   */
  private getLevel(category: string): LogLevel {
    if (this.moduleOverrides[category] !== undefined) {
      return this.moduleOverrides[category];
    }
    return this.globalLevel;
  }

  /**
   * Format log arguments with colored category prefix
   */
  private formatArgs(category: string, args: unknown[]): unknown[] {
    const color = this.COLORS[category] || this.COLORS[LogCategory.DEFAULT];
    const timestamp = new Date().toLocaleTimeString();
    return [
      `%c[${timestamp}] [${category}]`,
      `color: ${color}; font-weight: bold`,
      ...args
    ];
  }

  /**
   * Serialize arguments to a string for backend logging
   */
  private serializeArgs(args: unknown[]): string {
    return args.map(arg => {
      if (arg === null) return 'null';
      if (arg === undefined) return 'undefined';
      if (typeof arg === 'string') return arg;
      if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
      if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
      try {
        return JSON.stringify(arg, (_key, value) => {
          // Handle circular references and special types
          if (value instanceof HTMLElement) return `[HTMLElement: ${value.tagName}]`;
          if (typeof value === 'function') return '[Function]';
          if (typeof value === 'symbol') return value.toString();
          return value;
        }, 0);
      } catch (e) {
        return '[Unserializable]';
      }
    }).join(' ');
  }

  /**
   * Add a log entry to the buffer for backend
   */
  private bufferLog(level: LogLevelName, category: string, args: unknown[]): void {
    if (!this.backendLoggingEnabled) return;

    // Check if this level should be logged based on current settings
    const requiredLevel = this.LOG_LEVEL_VALUES[level] || LogLevel.NORMAL;
    const categoryLevel = this.getLevel(category);
    if (requiredLevel > categoryLevel) {
      return; // Skip logging this message - level too low
    }

    const entry: LogEntry = {
      level: level,
      category: category,
      message: this.serializeArgs(args),
      timestamp: new Date().toISOString()
    };

    this.logBuffer.push(entry);

    // Flush if buffer is full
    if (this.logBuffer.length >= this.LOG_BUFFER_SIZE) {
      this.flushLogs();
    }

    // Start flush timer if not already running
    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => this.flushLogs(), this.LOG_FLUSH_INTERVAL);
    }
  }

  /**
   * Flush log buffer to backend
   */
  private flushLogs(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.logBuffer.length === 0) return;

    const logsToSend = this.logBuffer.slice();
    this.logBuffer = [];

    // Send to backend asynchronously
    fetch(this.BACKEND_LOG_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ logs: logsToSend })
    }).catch(() => {
      // Silently fail - don't log errors about logging to avoid infinite loops
    });
  }

  // =========================================
  // Configuration Methods
  // =========================================

  /**
   * Set global log level
   * @param level - Log level to set
   */
  setLevel(level: LogLevel | string): void {
    if (typeof level === 'string') {
      this.globalLevel = this.parseLogLevel(level);
      this.setStorageItem('LOG_LEVEL', level.toUpperCase());
    } else {
      this.globalLevel = level;
      this.setStorageItem('LOG_LEVEL', this.levelToString(level));
    }
    console.log(`[Logger] Global level set to: ${this.levelToString(this.globalLevel)}`);
  }

  /**
   * Set log level for a specific category
   * @param category - Category name
   * @param level - Log level to set
   */
  setModuleLevel(category: LogCategory | string, level: LogLevel | string): void {
    const categoryKey = String(category);
    if (typeof level === 'string') {
      this.moduleOverrides[categoryKey] = this.parseLogLevel(level);
    } else {
      this.moduleOverrides[categoryKey] = level;
    }
    this.setStorageItem('LOG_MODULES', JSON.stringify(this.moduleOverrides));
    console.log(`[Logger] ${categoryKey} level set to: ${typeof level === 'string' ? level : this.levelToString(level)}`);
  }

  /**
   * Clear module-specific overrides
   * @param category - Optional category to clear (clears all if omitted)
   */
  clearModuleLevel(category?: LogCategory | string): void {
    if (category) {
      const categoryKey = String(category);
      delete this.moduleOverrides[categoryKey];
    } else {
      this.moduleOverrides = {};
    }
    this.setStorageItem('LOG_MODULES', JSON.stringify(this.moduleOverrides));
  }

  /**
   * Get current configuration
   * @returns Current logging configuration
   */
  getConfig(): LoggerConfig {
    const levelName = this.levelToString(this.globalLevel);
    return {
      globalLevel: levelName,
      moduleOverrides: Object.fromEntries(
        Object.entries(this.moduleOverrides).map(([k, v]) => [
          k,
          this.levelToString(v)
        ])
      )
    };
  }

  // =========================================
  // Logging Methods
  // =========================================

  /**
   * Log debug message (VERBOSE level only)
   * @param category - Log category
   * @param args - Arguments to log
   */
  debug(category: LogCategory | string, ...args: unknown[]): void {
    const categoryKey = String(category);
    // Buffer to backend only if VERBOSE level is enabled
    this.bufferLog('DEBUG', categoryKey, args);
    if (this.getLevel(categoryKey) >= LogLevel.VERBOSE) {
      console.log(...this.formatArgs(categoryKey, args));
    }
  }

  /**
   * Log info message (NORMAL and VERBOSE levels)
   * @param category - Log category
   * @param args - Arguments to log
   */
  info(category: LogCategory | string, ...args: unknown[]): void {
    const categoryKey = String(category);
    // Buffer to backend if NORMAL or VERBOSE level is enabled
    this.bufferLog('INFO', categoryKey, args);
    if (this.getLevel(categoryKey) >= LogLevel.NORMAL) {
      console.log(...this.formatArgs(categoryKey, args));
    }
  }

  /**
   * Log warning message (always shown unless OFF)
   * @param category - Log category
   * @param args - Arguments to log
   */
  warn(category: LogCategory | string, ...args: unknown[]): void {
    const categoryKey = String(category);
    // Buffer to backend if NORMAL or VERBOSE level is enabled
    this.bufferLog('WARN', categoryKey, args);
    if (this.getLevel(categoryKey) > LogLevel.OFF) {
      console.warn(...this.formatArgs(categoryKey, args));
    }
  }

  /**
   * Log error message (always shown unless OFF)
   * @param category - Log category
   * @param args - Arguments to log
   */
  error(category: LogCategory | string, ...args: unknown[]): void {
    const categoryKey = String(category);
    // Buffer to backend if NORMAL or VERBOSE level is enabled
    this.bufferLog('ERROR', categoryKey, args);
    if (this.getLevel(categoryKey) > LogLevel.OFF) {
      console.error(...this.formatArgs(categoryKey, args));
    }
  }

  // =========================================
  // Utility Methods
  // =========================================

  /**
   * Create a scoped logger for a specific category
   * @param category - Category name
   * @returns Scoped logger with debug/info/warn/error methods
   */
  scope(category: LogCategory | string): ScopedLogger {
    return {
      debug: (...args: unknown[]) => this.debug(category, ...args),
      info: (...args: unknown[]) => this.info(category, ...args),
      warn: (...args: unknown[]) => this.warn(category, ...args),
      error: (...args: unknown[]) => this.error(category, ...args)
    };
  }

  /**
   * Log with timing (useful for performance debugging)
   * @param category - Log category
   * @param label - Timer label
   * @returns Function to call to end timer and log duration
   */
  time(category: LogCategory | string, label: string): () => void {
    const start = performance.now();
    return () => {
      const duration = (performance.now() - start).toFixed(2);
      this.debug(category, `${label}: ${duration}ms`);
    };
  }

  // =========================================
  // Backend Logging Methods
  // =========================================

  /**
   * Enable or disable backend logging
   * @param enabled - Whether to enable backend logging
   */
  setBackendLogging(enabled: boolean): void {
    this.backendLoggingEnabled = enabled;
    console.log(`[Logger] Backend logging ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Force flush all buffered logs to backend immediately
   */
  flush(): void {
    this.flushLogs();
  }

  /**
   * Get number of logs currently in buffer
   * @returns Number of buffered logs
   */
  getBufferSize(): number {
    return this.logBuffer.length;
  }
}

// Export singleton instance
export const Logger = new LoggerClass();

// Export class for testing
export { LoggerClass };
